import type { Schemas } from '@ers/sdk';
import { keepPreviousData, type QueryKey, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, call, ifMatch } from '../../api/client';
import { apiHas } from '../../api/contract';
import type { ApiBody } from '../../components/data/apiTypes';
import { downloadFromApi, toQuery } from '../../components/data/download';
import { downloadPost } from '../../components/data/postDownload';
import { useCursorQuery } from '../../components/data/useCursorQuery';
import { useRouteProbe } from '../../components/data/useRouteProbe';
import { errorText } from '../../components/ui/ServerError';
import { useToast } from '../../components/ui/Toast';
import { useSession } from '../../app/session';

/*
 * Звіти, панелі показників і аналітика (модуль reports, М15): джерела й конструктор звітів, збережені звіти
 * (особисті, спільні, типові), словник показників із перевіркою якості даних, панелі за ролями з віджетами,
 * розсилки за розкладом і доставлені знімки. Кожен звіт виконується з правами того, хто дивиться: фінансові
 * поля без finance:read сервер прибирає з попередженням. Типи — з контракту (@ers/sdk), кеш — під ['reports'].
 */

export type ReportSource = Schemas['ReportSource'];
export type ReportField = ReportSource['fields'][number];
export type ReportConfig = Schemas['ReportConfigBody'];
export type SavedConfig = Schemas['ReportConfig'];
export type ReportResult = Schemas['ReportRunResult'];
export type ReportDefinition = Schemas['ReportDefinition'];
export type ReportMetric = Schemas['ReportMetric'];
export type ReportMetricDetail = Schemas['ReportMetricDetail'];
export type ReportMetricValue = Schemas['ReportMetricValue'];
export type QualityCheck = Schemas['ReportQualityCheck'];
export type MetricCheckResult = Schemas['ReportMetricCheckResult'];
export type ReportDashboard = Schemas['ReportDashboard'];
export type DashboardData = Schemas['ReportDashboardData'];
export type WidgetData = DashboardData['widgets'][number];
export type ReportWidget = Schemas['ReportWidget'];
export type ReportWidgetBody = Schemas['ReportWidgetBody'];
export type ReportSubscription = Schemas['ReportSubscription'];
export type ReportDelivery = Schemas['ReportDelivery'];

export type Filter = NonNullable<ReportConfig['filters']>[number];
export type Aggregate = NonNullable<ReportConfig['aggregates']>[number];
export type GroupBy = NonNullable<ReportConfig['groupBy']>[number];
export type Visualization = NonNullable<ReportConfig['visualization']>;
export type DefinitionBody = ApiBody<'/api/v1/reports/definitions', 'post'>;
export type DefinitionPatch = ApiBody<'/api/v1/reports/definitions/{id}', 'patch'>;
export type MetricBody = ApiBody<'/api/v1/reports/metrics', 'post'>;
export type MetricPatch = ApiBody<'/api/v1/reports/metrics/{id}', 'patch'>;
export type SubscriptionBody = ApiBody<'/api/v1/reports/subscriptions', 'post'>;
export type SubscriptionPatch = ApiBody<'/api/v1/reports/subscriptions/{id}', 'patch'>;

export const reportKeys = {
  all: ['reports'] as const,
  probe: ['probe', 'reports'] as const,
  sources: ['reports', 'sources'] as const,
  run: (config: unknown) => ['reports', 'run', config] as const,
  definitions: (q: Record<string, unknown>) => ['reports', 'definitions', q] as const,
  definition: (id: string) => ['reports', 'definition', id] as const,
  definitionRun: (id: string, period: unknown) => ['reports', 'definition', id, 'run', period] as const,
  metrics: (q: Record<string, unknown>) => ['reports', 'metrics', q] as const,
  metric: (id: string) => ['reports', 'metric', id] as const,
  dashboards: ['reports', 'dashboards'] as const,
  dashboardData: (id: string) => ['reports', 'dashboard', id, 'data'] as const,
  subscriptions: ['reports', 'subscriptions'] as const,
  deliveries: (q: Record<string, unknown>) => ['reports', 'deliveries', q] as const,
  delivery: (id: string) => ['reports', 'delivery', id] as const,
};

/** Розділ доступний: операція є в контракті, сервер відповідає на перелік джерел (users:read). */
export function useReportsAvailable(): boolean {
  const { can } = useSession();
  return useRouteProbe(reportKeys.probe, '/api/v1/reports/sources', { enabled: can('users:read') }).available;
}

/**
 * Хто діє у звітах — з сеансу (роль доступу, бізнес-ролі, області); остаточно перевіряє сервер. Права на
 * показники (змінювати, вносити значення) приходять у кожному показнику (canEdit, canEnterValues).
 */
export function useReportsActor() {
  const { me, isAdmin, can } = useSession();
  const roles = new Set(me.user.businessRoleCodes);
  return {
    userId: me.user.id,
    isAdmin,
    finance: can('finance:read'),
    canWrite: can('projects:write'),
    roles,
  };
}

export function useReportsMutation<V, D>(
  fn: (v: V) => Promise<D>,
  opts: { success?: string | ((d: D, v: V) => string | null); errorTitle?: string; also?: QueryKey[]; onSuccess?: (d: D, v: V) => void } = {},
) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: fn,
    onSuccess: (d, v) => {
      void qc.invalidateQueries({ queryKey: reportKeys.all });
      for (const k of opts.also ?? []) void qc.invalidateQueries({ queryKey: k });
      const msg = typeof opts.success === 'function' ? opts.success(d, v) : opts.success;
      if (msg) toast.success(msg);
      opts.onSuccess?.(d, v);
    },
    onError: opts.errorTitle ? (e) => toast.error(opts.errorTitle!, errorText(e)) : undefined,
  });
}

// ——— Джерела й конструктор ———

export function useReportSources() {
  return useQuery({ queryKey: reportKeys.sources, staleTime: 10 * 60_000, queryFn: async () => (await call(api.GET('/api/v1/reports/sources'))).data });
}

/** Виконання конфігурації конструктора (кеш — за самою конфігурацією; попередній результат тримається до нового). */
export function useRunReport(config: ReportConfig | null) {
  return useQuery({
    queryKey: reportKeys.run(config),
    enabled: !!config,
    staleTime: 30_000,
    retry: false,
    placeholderData: keepPreviousData,
    queryFn: () => call(api.POST('/api/v1/reports/run', { body: config! })),
  });
}

export function exportConfigCsv(config: ReportConfig, name: string) {
  return downloadPost('/api/v1/reports/export', { ...config, name }, `${name || 'report'}.csv`);
}

// ——— Збережені звіти ———

export function useDefinitions(q: { source?: string; mine?: boolean }) {
  return useCursorQuery<ReportDefinition>(reportKeys.definitions(q), async (cursor) =>
    call(api.GET('/api/v1/reports/definitions', { params: { query: { source: q.source, mine: q.mine ? 'true' : undefined, cursor, limit: 50 } } })),
  );
}

/** Усі доступні збережені звіти (для вибору у віджетах, розсилках і показниках). */
export function useAllDefinitions(enabled = true) {
  return useQuery({
    queryKey: reportKeys.definitions({ all: true }),
    enabled,
    staleTime: 60_000,
    queryFn: async () => {
      const out: ReportDefinition[] = [];
      let cursor: string | undefined;
      for (let i = 0; i < 10; i++) {
        const page = await call(api.GET('/api/v1/reports/definitions', { params: { query: { cursor, limit: 100 } } }));
        out.push(...page.data);
        if (!page.nextCursor) break;
        cursor = page.nextCursor;
      }
      return out;
    },
  });
}

export function useDefinition(id: string | null | undefined) {
  return useQuery({
    queryKey: reportKeys.definition(id ?? ''),
    enabled: !!id,
    staleTime: 30_000,
    queryFn: () => call(api.GET('/api/v1/reports/definitions/{id}', { params: { path: { id: id! } } })),
  });
}

export function useRunDefinition(id: string | null | undefined, period: { from?: string; to?: string } | null, enabled = true) {
  return useQuery({
    queryKey: reportKeys.definitionRun(id ?? '', period),
    enabled: !!id && enabled,
    staleTime: 30_000,
    retry: false,
    placeholderData: keepPreviousData,
    queryFn: () => call(api.POST('/api/v1/reports/definitions/{id}/run', { params: { path: { id: id! } }, body: period ?? {} })),
  });
}

export function downloadDefinitionCsv(id: string, name: string, period: { from?: string; to?: string } | null) {
  return downloadFromApi(`/api/v1/reports/definitions/${id}/export${toQuery({ from: period?.from, to: period?.to })}`, `${name || 'report'}.csv`);
}

export function useCreateDefinition() {
  return useReportsMutation((body: DefinitionBody) => call(api.POST('/api/v1/reports/definitions', { body })), { success: (r) => `Звіт «${r.definition.name}» збережено` });
}

export function usePatchDefinition() {
  return useReportsMutation(
    ({ id, version, body }: { id: string; version: number; body: DefinitionPatch }) => call(api.PATCH('/api/v1/reports/definitions/{id}', { params: { path: { id } }, body, headers: ifMatch(version) })),
    { success: 'Звіт збережено' },
  );
}

export function useArchiveDefinition() {
  return useReportsMutation((id: string) => call(api.POST('/api/v1/reports/definitions/{id}/archive', { params: { path: { id } } })), {
    success: (d) => `Звіт «${d.name}» архівовано`,
    errorTitle: 'Звіт не архівовано',
  });
}

// ——— Показники ———

type MetricsQuery = { status?: ReportMetric['status']; wave?: ReportMetric['wave']; includeArchived?: boolean };

/** Одна відповідь словника (перелік і право додавати) — спільний кеш для useMetrics і useCanCreateMetric. */
function metricsQueryOptions(q: MetricsQuery) {
  return {
    queryKey: reportKeys.metrics(q),
    staleTime: 60_000,
    queryFn: async () => call(api.GET('/api/v1/reports/metrics', { params: { query: { status: q.status, wave: q.wave, includeArchived: q.includeArchived || undefined } } })),
  };
}

export function useMetrics(q: MetricsQuery) {
  return useQuery({ ...metricsQueryOptions(q), select: (r) => r.data });
}

/** Право додавати показники до словника — від сервера (редактор словника), зокрема коли словник порожній. */
export function useCanCreateMetric(q: MetricsQuery = {}) {
  return useQuery({ ...metricsQueryOptions(q), select: (r) => r.canCreate }).data === true;
}

/** Вбудовані розрахунки показників (calc.kind = builtin) — перелік із сервера. */
export function useMetricBuiltins(enabled = true) {
  return useQuery({
    queryKey: [...reportKeys.metrics({}), 'builtins'] as const,
    enabled: enabled && apiHas('GET /api/v1/reports/metrics/builtins'),
    staleTime: 10 * 60_000,
    queryFn: async () => (await call(api.GET('/api/v1/reports/metrics/builtins'))).data,
  });
}

export function useMetric(id: string | undefined) {
  return useQuery({
    queryKey: reportKeys.metric(id ?? ''),
    enabled: !!id,
    staleTime: 30_000,
    queryFn: () => call(api.GET('/api/v1/reports/metrics/{id}', { params: { path: { id: id! } } })),
  });
}

export function useCreateMetric() {
  return useReportsMutation((body: MetricBody) => call(api.POST('/api/v1/reports/metrics', { body })), { success: (m) => `Показник «${m.name}» додано чернеткою` });
}

export function usePatchMetric() {
  return useReportsMutation(
    ({ id, version, body }: { id: string; version: number; body: MetricPatch }) => call(api.PATCH('/api/v1/reports/metrics/{id}', { params: { path: { id } }, body, headers: ifMatch(version) })),
    { success: (m) => (m.status === 'draft' ? 'Показник збережено — він у чернетці до нової перевірки якості' : 'Показник збережено') },
  );
}

export function useMetricStatus() {
  return useReportsMutation(
    ({ id, status, reason }: { id: string; status: 'paused' | 'archived' | 'draft'; reason?: string | null }) => call(api.POST('/api/v1/reports/metrics/{id}/status', { params: { path: { id } }, body: { status, reason: reason ?? null } })),
    { success: (m) => (m.status === 'paused' ? 'Показник призупинено' : m.status === 'archived' ? 'Показник архівовано' : 'Показник повернуто в чернетку') },
  );
}

export function useQualityCheck() {
  return useReportsMutation(({ id, periodKey }: { id: string; periodKey: string | null }) => call(api.POST('/api/v1/reports/metrics/{id}/quality-check', { params: { path: { id } }, body: { periodKey } })));
}

export function useActivateMetric() {
  return useReportsMutation(({ id, periodKey }: { id: string; periodKey: string | null }) => call(api.POST('/api/v1/reports/metrics/{id}/activate', { params: { path: { id } }, body: { periodKey } })), {
    success: (m) => `Показник «${m.name}» запущено`,
  });
}

export function useCalculateMetric() {
  return useReportsMutation(
    ({ id, periodKey, replaceManual }: { id: string; periodKey: string | null; replaceManual?: boolean }) =>
      call(api.POST('/api/v1/reports/metrics/{id}/calculate', { params: { path: { id } }, body: { periodKey, replaceManual } })),
    { success: (v) => `Значення за ${v.periodKey} розраховано` },
  );
}

export function useEnterMetricValue() {
  return useReportsMutation(
    ({ id, body }: { id: string; body: { periodKey: string; value: number; evidence: string; evidenceUrl?: string | null; note?: string | null } }) =>
      call(api.POST('/api/v1/reports/metrics/{id}/values', { params: { path: { id } }, body })),
    { success: (v) => `Значення за ${v.periodKey} внесено` },
  );
}

// ——— Панелі ———

export function useDashboards() {
  return useQuery({ queryKey: reportKeys.dashboards, staleTime: 60_000, queryFn: () => call(api.GET('/api/v1/reports/dashboards')) });
}

export function useDashboardData(id: string | null) {
  return useQuery({
    queryKey: reportKeys.dashboardData(id ?? ''),
    enabled: !!id,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
    queryFn: () => call(api.GET('/api/v1/reports/dashboards/{id}/data', { params: { path: { id: id! } } })),
  });
}

export function useCreatePersonalDashboard() {
  return useReportsMutation((body: { roleKey: string; name?: string; widgets?: ReportWidgetBody[] }) => call(api.POST('/api/v1/reports/dashboards', { body })), {
    success: 'Створено ваші налаштування панелі — зміни бачите лише ви',
  });
}

export function usePatchDashboard() {
  return useReportsMutation(
    ({ id, version, body }: { id: string; version: number; body: { name?: string; widgets?: ReportWidgetBody[] } }) =>
      call(api.PATCH('/api/v1/reports/dashboards/{id}', { params: { path: { id } }, body, headers: ifMatch(version) })),
    { success: 'Панель збережено' },
  );
}

export function useResetDashboard() {
  return useReportsMutation((id: string) => call(api.POST('/api/v1/reports/dashboards/{id}/reset', { params: { path: { id } } })), {
    success: 'Повернуто типову панель ролі',
    errorTitle: 'Не вдалося скинути налаштування',
  });
}

// ——— Розсилки й знімки ———

export function useSubscriptions() {
  return useQuery({ queryKey: reportKeys.subscriptions, staleTime: 60_000, queryFn: async () => (await call(api.GET('/api/v1/reports/subscriptions'))).data });
}

export function useCreateSubscription() {
  return useReportsMutation((body: SubscriptionBody) => call(api.POST('/api/v1/reports/subscriptions', { body })), { success: (s) => `Розсилку «${s.name}» створено` });
}

export function usePatchSubscription() {
  return useReportsMutation(
    ({ id, version, body }: { id: string; version: number; body: SubscriptionPatch }) => call(api.PATCH('/api/v1/reports/subscriptions/{id}', { params: { path: { id } }, body, headers: ifMatch(version) })),
    { success: (s, v) => (v.body.active === false ? `Розсилку «${s.name}» вимкнено` : v.body.active === true ? `Розсилку «${s.name}» увімкнено` : 'Розсилку збережено'), errorTitle: 'Розсилку не змінено' },
  );
}

export function useArchiveSubscription() {
  return useReportsMutation((id: string) => call(api.POST('/api/v1/reports/subscriptions/{id}/archive', { params: { path: { id } } })), { success: 'Розсилку архівовано', errorTitle: 'Розсилку не архівовано' });
}

export function useRunSubscriptionNow() {
  return useReportsMutation((id: string) => call(api.POST('/api/v1/reports/subscriptions/{id}/run-now', { params: { path: { id } } })), {
    success: (r) => `Надіслано знімків: ${r.ready}${r.skipped ? `, пропущено: ${r.skipped}` : ''}`,
    errorTitle: 'Розсилку не надіслано',
  });
}

export function useDeliveries(subscriptionId?: string) {
  return useCursorQuery<ReportDelivery>(reportKeys.deliveries({ subscriptionId }), async (cursor) =>
    call(api.GET('/api/v1/reports/deliveries', { params: { query: { subscriptionId, cursor, limit: 50 } } })),
  );
}

export function useDelivery(id: string | undefined) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: reportKeys.delivery(id ?? ''),
    enabled: !!id,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const d = await call(api.GET('/api/v1/reports/deliveries/{id}', { params: { path: { id: id! } } }));
      // Перегляд позначає знімок прочитаним — оновлюємо перелік.
      void qc.invalidateQueries({ queryKey: ['reports', 'deliveries'] });
      return d;
    },
  });
}
